import { useMemo } from 'react';
import { startOfWeek, endOfWeek, eachDayOfInterval, format, isToday as checkIsToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { AppointmentListItem, DoctorAvailabilityException } from '@/api';
import { TimeGridRuler } from './TimeGridRuler';
import { DayColumn } from './DayColumn';
import { cn } from '@/utils/cn';

export interface WeeklyGridProps {
  startDate: Date;
  appointments: AppointmentListItem[];
  exceptions: DoctorAvailabilityException[];
  isLoading?: boolean;
  startHour?: number;
  endHour?: number;
  hourHeight?: number;
  onSelectAppointment: (appointment: AppointmentListItem) => void;
  onBlockSlot?: (date: Date, startTime: string, endTime: string) => void;
  onUnblockSlot?: (exceptionId: string) => void;
}

export function WeeklyGrid({
  startDate,
  appointments,
  exceptions,
  isLoading = false,
  startHour = 7,
  endHour = 22,
  hourHeight = 72,
  onSelectAppointment,
  onBlockSlot,
  onUnblockSlot,
}: WeeklyGridProps) {
  // Determina o intervalo de segunda a domingo
  const days = useMemo(() => {
    const monday = startOfWeek(startDate, { weekStartsOn: 1 });
    const sunday = endOfWeek(startDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: monday, end: sunday });
  }, [startDate]);

  return (
    <div className="relative flex flex-col rounded-xl border border-border bg-card shadow-xs overflow-hidden">
      {/* Overlay de carregamento sutil se isLoading */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-40 flex items-center justify-center">
          <div className="flex items-center gap-2 rounded-lg bg-card px-4 py-2 border border-border shadow-md">
            <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="text-xs font-medium text-foreground">Atualizando grade...</span>
          </div>
        </div>
      )}

      {/* Container com scroll horizontal em telas menores para não espremer as colunas */}
      <div className="overflow-x-auto">
        <div className="min-w-[850px]">
          {/* Cabeçalho das Colunas: Régua de Horas + 7 Dias */}
          <div className="flex border-b border-border bg-muted/30 sticky top-0 z-30">
            {/* Espaço reservado para alinhar com TimeGridRuler */}
            <div className="w-14 sm:w-16 flex-shrink-0 border-r border-border/60 py-3 text-center">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Hora
              </span>
            </div>

            {/* 7 Colunas de Dias da Semana */}
            <div className="grid grid-cols-7 flex-1">
              {days.map((day) => {
                const isDayToday = checkIsToday(day);
                const dayOfWeekStr = format(day, 'EEE', { locale: ptBR }).replace('.', '');
                const dayOfMonthStr = format(day, 'd');
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                return (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      'py-2.5 px-1 sm:px-2 text-center border-r border-border/60 last:border-r-0 transition-colors',
                      isDayToday && 'bg-primary/[0.04]',
                      isWeekend && !isDayToday && 'bg-muted/20'
                    )}
                  >
                    <span
                      className={cn(
                        'block text-[11px] uppercase font-semibold tracking-wider text-muted-foreground',
                        isDayToday && 'text-primary'
                      )}
                    >
                      {dayOfWeekStr}
                    </span>

                    <div className="mt-1 flex items-center justify-center">
                      <span
                        className={cn(
                          'inline-flex items-center justify-center text-sm sm:text-base font-bold transition-all',
                          isDayToday
                            ? 'h-7 w-7 rounded-full bg-primary text-primary-foreground shadow-sm'
                            : 'text-foreground'
                        )}
                      >
                        {dayOfMonthStr}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Corpo da Grade: Régua + Colunas de Dias */}
          <div className="flex relative max-h-[720px] overflow-y-auto scrollbar-thin">
            {/* Eixo vertical de tempo (07:00 às 22:00) */}
            <TimeGridRuler
              startHour={startHour}
              endHour={endHour}
              hourHeight={hourHeight}
            />

            {/* 7 Colunas de Agendamentos & Disponibilidade */}
            <div className="grid grid-cols-7 flex-1">
              {days.map((day) => {
                const isDayToday = checkIsToday(day);

                return (
                  <DayColumn
                    key={day.toISOString()}
                    date={day}
                    isToday={isDayToday}
                    appointments={appointments}
                    exceptions={exceptions}
                    startHour={startHour}
                    endHour={endHour}
                    hourHeight={hourHeight}
                    onSelectAppointment={onSelectAppointment}
                    onBlockSlot={onBlockSlot}
                    onUnblockSlot={onUnblockSlot}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
